import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import PermissionTree from '../../components/PermissionTree';
import { roleService } from '../../services/roleService';
import { Permission } from '../../types';
import { useModule } from '../../context/ModuleContext';
import { filterPermissionsByModule } from '../../utils/permissionModuleVisibility';
import '../../components/PermissionTree.css';
import '../Products/ProductCategoryCreatePage.css';
import { parseApiError } from '../../utils/errorUtils';

interface RoleFormData {
  name: string;
  description: string;
  permissionIds: number[];
}

interface RoleFormPageProps {
  mode: 'create' | 'edit';
  roleId?: string;
}

const RoleFormPage: React.FC<RoleFormPageProps> = ({ mode, roleId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { moduleCode } = useModule();
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissionIds: [],
  });
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const visiblePermissions = filterPermissionsByModule(allPermissions, moduleCode);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    roleService
      .getPermissions()
      .then((res) => {
        const data = res.data?.data;
        setAllPermissions(Array.isArray(data) ? data : []);
      })
      .catch(() => setError(t('role.failedToLoad')));
  }, [t]);

  useEffect(() => {
    if (mode !== 'edit' || !roleId) return;
    setLoading(true);
    roleService
      .getRoleById(roleId)
      .then((res) => {
        const role = res.data?.data;
        if (role) {
          if (role.isSystem) {
            navigate(`/roles/${roleId}`, { replace: true });
            return;
          }
          setFormData({
            name: role.name,
            description: role.description,
            permissionIds: role.permissions.map((p) => p.id),
          });
        }
      })
      .catch(() => setError(t('role.failedToLoad')))
      .finally(() => setLoading(false));
  }, [mode, roleId, t, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t('role.roleNameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        await roleService.createRole(formData);
        navigate('/roles', {
          state: { message: t('role.roleCreatedSuccess') },
        });
      } else if (roleId) {
        await roleService.updateRole(roleId, formData);
        navigate(`/roles/${roleId}`, {
          state: { message: t('role.roleUpdatedSuccess') },
        });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, mode === 'create' ? t('role.failedToCreate') : t('role.failedToUpdate')).message);
      setSaving(false);
    }
  };

  const backPath = mode === 'create' ? '/roles' : `/roles/${roleId}`;

  if (loading) {
    return (
      <Layout>
        <div className="create-container">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const visibleIds = new Set(visiblePermissions.map((p) => p.id));
  const selectedCount = formData.permissionIds.filter((id) => visibleIds.has(id)).length;
  const totalCount = visiblePermissions.length;

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(backPath)}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {mode === 'create' ? t('role.createTitle') : t('role.editTitle')}
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate(backPath)}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="role-form"
              className="ims-btn ims-btn-primary"
              disabled={saving}
            >
              {saving ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <><Save size={15} strokeWidth={2} />{mode === 'create' ? t('role.createTitle') : t('common.saveChanges')}</>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <form id="role-form" onSubmit={handleSubmit}>
          <div className="create-content">
            {/* ── Left column ── */}
            <div className="create-main-section">

              {/* Role details card */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('role.roleDetails')}</h2>
                </div>
                <div className="create-card-body">
                  <div className="form-group">
                    <label className="form-label">
                      {t('role.roleName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="e.g. Inventory Manager"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('role.description')}</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, description: e.target.value }))
                      }
                      placeholder="Describe what this role is for…"
                    />
                  </div>
                </div>
              </div>

              {/* Permissions card with TreeView */}
              <div className="create-card">
                <div className="create-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="create-card-title">
                    {t('role.permissions')}
                    <span
                      className="badge badge-info"
                      style={{ marginInlineStart: '10px', fontSize: '11px' }}
                    >
                      {selectedCount} / {totalCount}
                    </span>
                  </h2>
                </div>
                <div className="create-card-body">
                  {visiblePermissions.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                      {t('role.noPermissionsAssigned')}
                    </p>
                  ) : (
                    <PermissionTree
                      allPermissions={visiblePermissions}
                      selectedIds={formData.permissionIds}
                      onChange={(ids) =>
                        setFormData((p) => ({ ...p, permissionIds: ids }))
                      }
                    />
                  )}
                </div>
              </div>

            </div>

            {/* ── Right sidebar ── */}
            <div className="create-sidebar">
              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('role.summary')}</h3>
                </div>
                <div className="create-card-body">
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    This role will grant access to <strong>{selectedCount}</strong> out of{' '}
                    <strong>{totalCount}</strong> available permission(s).
                  </p>
                </div>
              </div>

              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('role.required')}</h3>
                </div>
                <div className="create-card-body">
                  <ul style={{ paddingInlineStart: '16px', margin: 0, fontSize: '13px', lineHeight: '1.8' }}>
                    <li>{t('role.roleName')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default RoleFormPage;
